import React, { useMemo, useState } from "react";
import { ArrowLeft, ChevronRight, HelpCircle, MapPin, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import FeatureStatusBadge, { FeatureStatus } from "./FeatureStatusBadge";
import SidePanel from "../../../components/ui/SidePanel";
import { useLanguage } from "../../../components/ui/LanguageProvider";
import type { Language } from "../../../components/ui/LanguageProvider";

type GuideNode = {
  key: string;
  title: string;
  question?: string;
  content?: string[];
  route?: string;
  featureStatus?: FeatureStatus;
  options?: Array<{ label: string; to: string }>;
};

const GUIDE_NODES: Record<string, GuideNode> = {
  root: {
    key: "root",
    title: "CRM 사용 가이드",
    question: "무엇이 어렵나요?",
    options: [
      { label: "신규 문의 처리", to: "lead" },
      { label: "Opportunity 전환", to: "opportunity" },
      { label: "Pipeline 단계 이동", to: "pipeline" },
      { label: "Project 생성", to: "project" },
      { label: "STEP 관리", to: "step" },
      { label: "Input / Output 문서", to: "documents" },
      { label: "메일 / DocuSign 발송", to: "send" },
      { label: "대시보드와 리포트", to: "dashboard" },
      { label: "설정 / 마스터 관리", to: "admin" },
    ],
  },
  lead: {
    key: "lead",
    title: "신규 문의 처리",
    question: "Lead Inbox에서 무엇을 해야 하나요?",
    route: "/crm/leads",
    featureStatus: "development",
    content: [
      "Lead Inbox에서 새 문의를 확인합니다.",
      "회사명, 담당자, 문의 내용, 관심 솔루션을 먼저 확인합니다.",
      "담당자를 배정하고 1차 연락 상태로 변경합니다.",
      "고객사/니즈/후속 미팅 가능성이 확인되면 Opportunity로 전환합니다.",
    ],
  },
  opportunity: {
    key: "opportunity",
    title: "Opportunity 전환",
    question: "언제 Opportunity로 바꾸나요?",
    route: "/crm/opportunities",
    featureStatus: "active",
    content: [
      "고객사, 담당자, 니즈, 후속 미팅 가능성 중 2개 이상 확인되면 전환합니다.",
      "전환 후 Opportunities 목록에서 Pipeline 진행 현황을 관리합니다.",
      "Flow Builder에서 Opportunity 단계별 실행 로직과 문서 자동화를 설정합니다.",
    ],
  },
  pipeline: {
    key: "pipeline",
    title: "Pipeline 단계 이동",
    question: "Pipeline에서 다음 액션은 무엇인가요?",
    route: "/crm/pipeline",
    featureStatus: "active",
    content: [
      "카드에서 고객사, 현재 단계, 담당자, 마감일을 확인합니다.",
      "단계 이동 버튼을 눌러 다음 단계로 이동합니다.",
      "단계 이동 시 생성할 산출 문서를 선택할 수 있습니다.",
      "Flow Builder에서 단계별 필요 문서와 산출 문서를 관리합니다.",
    ],
  },
  project: {
    key: "project",
    title: "Project 생성",
    question: "Project는 언제 생성하나요?",
    route: "/crm/opportunities",
    featureStatus: "development",
    content: [
      "고객 자료 수집, 제안서, ROI, 견적서, 계약서가 필요해지는 순간 Project를 만듭니다.",
      "Project Type을 선택하면 추후 Step Template을 자동 적용하는 구조로 확장합니다.",
      "현재 Project Workspace는 Draft 상태입니다.",
    ],
  },
  step: {
    key: "step",
    title: "STEP 관리",
    question: "STEP에서는 무엇을 봐야 하나요?",
    route: "/crm/opportunities",
    featureStatus: "development",
    content: [
      "현재 STEP에서 필요한 Input 문서와 만들어야 할 Output 문서를 확인합니다.",
      "STEP 완료 전 누락된 Input/Output이 있으면 하이라이트로 안내하는 구조가 필요합니다.",
      "Flow Builder는 Project Step 자동화 마스터 역할을 합니다.",
    ],
  },
  documents: {
    key: "documents",
    title: "Input / Output 문서",
    question: "문서는 어디에서 관리하나요?",
    route: "/crm/documents",
    featureStatus: "development",
    content: [
      "Documents에서 모든 문서를 통합 관리합니다.",
      "Pipeline 단계 이동 또는 Project Step에서 산출 문서를 선택합니다.",
      "견적서, 계약서, NDA, MOU, Invoice 같은 문서 자동화 페이지와 연결됩니다.",
    ],
  },
  send: {
    key: "send",
    title: "메일 / DocuSign 발송",
    question: "발송 기능은 지금 가능한가요?",
    route: "/crm/integrations",
    featureStatus: "coming_soon",
    content: [
      "메일 템플릿, DocuSign, Slack, Calendar, Drive는 UI Draft와 설정 화면을 먼저 제공합니다.",
      "실제 발송/전자서명/동기화는 아직 개발 중 상태로 처리합니다.",
      "추후 발송 이력과 외부 연동 로그를 DB에 저장하는 구조로 확장합니다.",
    ],
  },
  dashboard: {
    key: "dashboard",
    title: "대시보드와 리포트",
    question: "대시보드는 무엇을 확인하나요?",
    route: "/crm/dashboard",
    featureStatus: "active",
    content: [
      "오늘 해야 할 일, 지연 건, Pipeline Forecast를 우선 확인합니다.",
      "위젯은 쿼리 기반으로 추가/수정/삭제할 수 있습니다.",
      "고급 사용자는 Query Editor로 조회 조건을 조정합니다.",
    ],
  },
  admin: {
    key: "admin",
    title: "설정 / 마스터 관리",
    question: "관리자는 무엇을 설정하나요?",
    route: "/crm/admin",
    featureStatus: "development",
    content: [
      "Pipeline Stage, Project Template, Document Template, Feature Flag를 관리합니다.",
      "Help Assistant의 질문과 선택지도 guide_nodes / guide_edges 구조로 DB화할 수 있습니다.",
      "현재는 Draft UI이며 실제 관리 기능은 단계적으로 연결해야 합니다.",
    ],
  },
};

// 영문 표시용 — 키별 title/question/content/option 라벨(순서는 GUIDE_NODES 와 동일).
const GUIDE_NODES_EN: Record<string, { title: string; question?: string; content?: string[]; options?: string[] }> = {
  root: {
    title: "CRM user guide",
    question: "What are you having trouble with?",
    options: ["Handling new inquiries", "Converting to Opportunity", "Moving Pipeline stages", "Creating a Project", "Managing STEPs", "Input / Output documents", "Sending mail / DocuSign", "Dashboards and reports", "Settings / master data"],
  },
  lead: {
    title: "Handling new inquiries",
    question: "What should I do in the Lead Inbox?",
    content: [
      "Check new inquiries in the Lead Inbox.",
      "First review the company, contact, inquiry details, and solutions of interest.",
      "Assign an owner and change the status to first contact.",
      "Once the customer, needs, and follow-up meeting potential are confirmed, convert to an Opportunity.",
    ],
  },
  opportunity: {
    title: "Converting to Opportunity",
    question: "When should I convert to an Opportunity?",
    content: [
      "Convert when at least 2 of customer, contact, needs, and follow-up meeting potential are confirmed.",
      "After converting, manage Pipeline progress from the Opportunities list.",
      "Configure per-stage execution logic and document automation for Opportunities in Flow Builder.",
    ],
  },
  pipeline: {
    title: "Moving Pipeline stages",
    question: "What is the next action in the Pipeline?",
    content: [
      "Check the customer, current stage, owner, and due date on the card.",
      "Click the move stage button to go to the next stage.",
      "You can choose output documents to generate when moving stages.",
      "Manage required and output documents per stage in Flow Builder.",
    ],
  },
  project: {
    title: "Creating a Project",
    question: "When should I create a Project?",
    content: [
      "Create a Project as soon as you need customer data collection, a proposal, ROI, a quote, or a contract.",
      "Selecting a Project Type will later apply a Step Template automatically.",
      "The Project Workspace is currently in Draft.",
    ],
  },
  step: {
    title: "Managing STEPs",
    question: "What should I look at in a STEP?",
    content: [
      "Check the Input documents needed and the Output documents to create in the current STEP.",
      "Missing Inputs/Outputs before completing a STEP should be highlighted.",
      "Flow Builder serves as the master for Project Step automation.",
    ],
  },
  documents: {
    title: "Input / Output documents",
    question: "Where are documents managed?",
    content: [
      "All documents are managed together in Documents.",
      "Choose output documents when moving Pipeline stages or in a Project Step.",
      "Linked to document automation pages such as quotes, contracts, NDA, MOU, and Invoice.",
    ],
  },
  send: {
    title: "Sending mail / DocuSign",
    question: "Can I send now?",
    content: [
      "Mail templates, DocuSign, Slack, Calendar, and Drive provide draft UI and settings screens first.",
      "Actual sending/e-signature/sync is still in development.",
      "Sending history and external integration logs will later be stored in the DB.",
    ],
  },
  dashboard: {
    title: "Dashboards and reports",
    question: "What does the dashboard show?",
    content: [
      "Check today's tasks, overdue items, and Pipeline Forecast first.",
      "Widgets can be added/edited/deleted based on queries.",
      "Advanced users can adjust query conditions in the Query Editor.",
    ],
  },
  admin: {
    title: "Settings / master data",
    question: "What do admins configure?",
    content: [
      "Manage Pipeline Stages, Project Templates, Document Templates, and Feature Flags.",
      "Help Assistant questions and options can also be stored in the DB as guide_nodes / guide_edges.",
      "This is currently Draft UI; actual admin features need to be connected step by step.",
    ],
  },
};

function localizeNode(node: GuideNode, language: Language): GuideNode {
  if (language !== "en") return node;
  const en = GUIDE_NODES_EN[node.key];
  if (!en) return node;
  return {
    ...node,
    title: en.title,
    question: en.question ?? node.question,
    content: en.content ?? node.content,
    options: node.options?.map((o, i) => ({ ...o, label: en.options?.[i] ?? o.label })),
  };
}

const PAGE_RECOMMENDATIONS: Array<{ pattern: string; nodes: string[] }> = [
  { pattern: "/crm/leads", nodes: ["lead", "opportunity"] },
  { pattern: "/crm/pipeline", nodes: ["pipeline", "project", "documents"] },
  { pattern: "/crm/opportunities", nodes: ["project", "step", "documents"] },
  { pattern: "/crm/documents", nodes: ["documents", "send"] },
  { pattern: "/crm/dashboard", nodes: ["dashboard", "pipeline"] },
  { pattern: "/crm/reports", nodes: ["dashboard"] },
  { pattern: "/crm/integrations", nodes: ["send", "admin"] },
  { pattern: "/crm/admin", nodes: ["admin", "step"] },
];

// 여는 방법만 컨트롤드로 — 상단바 "도움말"이 트리거. 드로어 내용/동작은 그대로.
// 모양(Vault UI): 420px · 머리(아이콘 + 도움말 + 부제) · 검색 → 현재 화면 추천(연파랑 카드) → 주제 목록(40px 줄).
export default function HelpAssistantDrawer({ open: openProp, onOpenChange }: { open?: boolean; onOpenChange?: (o: boolean) => void }) {
  const { t, language } = useLanguage();
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = (v: boolean) => { if (onOpenChange) onOpenChange(v); else setOpenState(v); };
  const [history, setHistory] = useState<string[]>(["root"]);
  // 주제 검색 — 화면 안에서만 거른다(서버 호출 없음).
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const currentKey = history[history.length - 1] ?? "root";
  const current = localizeNode(GUIDE_NODES[currentKey] ?? GUIDE_NODES.root, language);

  const recommended = useMemo(() => {
    const match = PAGE_RECOMMENDATIONS.find((item) => location.pathname.startsWith(item.pattern));
    return (match?.nodes ?? ["lead", "pipeline", "project"]).map((key) => GUIDE_NODES[key]).filter(Boolean).map((node) => localizeNode(node, language));
  }, [location.pathname, language]);

  // 검색 결과 — 루트 주제(선택지) 라벨로 거른다. 같은 주제의 한/영 이름을 모두 본다.
  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return [];
    const root = GUIDE_NODES.root;
    return (root.options ?? []).filter((option, i) => {
      const en = GUIDE_NODES_EN.root.options?.[i] ?? "";
      return `${option.label} ${en}`.toLowerCase().includes(q);
    }).map((option) => localizeNode(GUIDE_NODES[option.to], language)).filter(Boolean);
  }, [q, language]);

  function goTo(key: string) {
    setQuery("");
    setHistory((prev) => [...prev, key]);
  }

  function goBack() {
    setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }

  function resetAndClose() {
    setOpen(false);
    setHistory(["root"]);
    setQuery("");
  }

  return (
    <SidePanel
      open={open}
      onClose={resetAndClose}
      width={420}
      title={<span className="inline-flex items-center gap-2"><HelpCircle size={18} className="text-primary" aria-hidden />{t("도움말", "Help")}</span>}
      subtitle={t("이 화면에서 할 수 있는 일", "What you can do on this screen")}
    >
      <div className="ds-scope grid gap-3.5">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("궁금한 것 검색", "Search help")}
            aria-label={t("도움말 주제 검색", "Search help topics")}
            className="h-9 w-full rounded-card-sm border border-line bg-white pl-[34px] pr-3 text-[13px] text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--primary-subtle)]"
          />
        </div>

        {q ? (
          <section className="grid gap-1.5" aria-label={t("검색 결과", "Search results")}>
            {searchResults.length ? searchResults.map((node) => (
              <TopicButton key={node.key} label={node.title} status={node.featureStatus} onClick={() => goTo(node.key)} />
            )) : <div className="px-1 py-3 text-[13px] text-ink-muted">{t("검색 결과 없음", "No results")}</div>}
          </section>
        ) : (
          <>
            {history.length === 1 ? (
              <section className="rounded-card border border-primary-line bg-primary-subtle px-3.5 py-3">
                <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-primary-strong"><MapPin size={12} aria-hidden /> {t("현재 화면 추천", "Suggested for this screen")}</div>
                <div className="grid gap-1.5">
                  {recommended.map((node) => (
                    <TopicButton key={node.key} label={node.title} hint={node.question} status={node.featureStatus} onClick={() => goTo(node.key)} />
                  ))}
                </div>
              </section>
            ) : null}

            <section className="grid gap-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[16px] font-semibold text-ink">{current.title}</div>
                  {current.question ? <div className="mt-1 text-[13px] text-ink-muted">{current.question}</div> : null}
                </div>
                {current.featureStatus ? <FeatureStatusBadge status={current.featureStatus} size="sm" /> : null}
              </div>

              {current.options ? (
                <div className="grid gap-1.5">
                  {current.options.map((option) => (
                    <TopicButton key={option.to} label={option.label} onClick={() => goTo(option.to)} />
                  ))}
                </div>
              ) : null}

              {current.content ? (
                <div className="grid gap-2">
                  {current.content.map((line, index) => (
                    <div key={line} className="grid grid-cols-[22px_1fr] items-start gap-2 text-[13px] leading-relaxed text-ink-data">
                      <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-pill bg-primary-subtle font-num text-[12px] font-semibold text-primary-strong">{index + 1}</span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* 보조(뒤로) → 주요(관련 화면으로 이동) — 주요는 오른쪽 끝 하나 */}
              {history.length > 1 || current.route ? (
                <div className="flex items-center justify-between gap-2">
                  <button type="button" onClick={goBack} disabled={history.length <= 1}
                    className="inline-flex h-8 items-center gap-1.5 rounded-control border border-line bg-white px-3 text-[13px] text-ink-data hover:bg-surface-hover disabled:opacity-45">
                    <ArrowLeft size={14} aria-hidden />
                    {t("뒤로", "Back")}
                  </button>
                  {current.route ? (
                    <button type="button" onClick={() => { navigate(current.route!); resetAndClose(); }}
                      className="inline-flex h-8 items-center rounded-control bg-primary px-3 text-[13px] font-semibold text-white hover:bg-primary-hover">
                      {t("관련 화면으로 이동", "Go to related screen")}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>
          </>
        )}
      </div>
    </SidePanel>
  );
}

// 주제 줄 — 40px · hairline-strong 테두리 · 오른쪽 chevron(상태 배지가 있으면 배지 + chevron).
function TopicButton({ label, hint, status, onClick }: { label: string; hint?: string; status?: FeatureStatus; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      className="flex h-10 w-full items-center gap-2.5 rounded-card-sm border border-hairline-strong bg-white px-3 text-left text-[13px] font-medium text-ink hover:border-line hover:bg-surface-hover"
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {status ? <FeatureStatusBadge status={status} size="sm" /> : null}
      <ChevronRight size={14} className="flex-shrink-0 text-ink-disabled" aria-hidden />
    </button>
  );
}
